import { OnboardingDebtScheduleStateEnum } from "@boundless/bl-api-client/customer/models/OnboardingDebtScheduleStateEnum"
import { Clock, CircleX, FileText } from "lucide-react"

import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"

import type { LucideIcon } from "lucide-react"

type DebtStateChoiceProps = {
  value: OnboardingDebtScheduleStateEnum | null
  onChange: (value: OnboardingDebtScheduleStateEnum) => void
  /** Why skipping is unavailable, or null when the borrower may skip. */
  skipBlockedReason: string | null
}

const OPTIONS: {
  value: OnboardingDebtScheduleStateEnum
  icon: LucideIcon
  title: string
  description: string
}[] = [
  {
    value: OnboardingDebtScheduleStateEnum.submitted,
    icon: FileText,
    title: "Yes, we have debt to report",
    description: "Add lenders, balances, and terms",
  },
  {
    value: OnboardingDebtScheduleStateEnum["no-debt"],
    icon: CircleX,
    title: "No, this business has no debt",
    description: "Lenders will see that declaration",
  },
  {
    value: OnboardingDebtScheduleStateEnum.skipped,
    icon: Clock,
    title: "Not sure yet",
    description: "Skip for now, come back later",
  },
]

export function DebtStateChoice({
  value,
  onChange,
  skipBlockedReason,
}: DebtStateChoiceProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Debt reporting"
      className="grid gap-4 sm:grid-cols-3"
    >
      {OPTIONS.map(({ value: optionValue, icon: Icon, title, description }) => {
        const isSelected = value === optionValue
        const isDisabled =
          optionValue === OnboardingDebtScheduleStateEnum.skipped &&
          skipBlockedReason !== null
        const select = () => {
          if (!isDisabled) {
            onChange(optionValue)
          }
        }

        return (
          <Card
            key={optionValue}
            data-testid={`debt-state-${optionValue}`}
            role="radio"
            tabIndex={isDisabled ? -1 : 0}
            aria-checked={isSelected}
            aria-disabled={isDisabled}
            onClick={select}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault()
                select()
              }
            }}
            className={cn(
              "cursor-pointer gap-3 rounded-lg border py-3.5 shadow-none transition-colors",
              "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
              isSelected
                ? "border-primary bg-accent/40"
                : "border-input hover:border-primary",
              isDisabled && "hover:border-input cursor-not-allowed opacity-50",
            )}
          >
            <CardContent className="flex flex-col gap-3 px-3.5">
              <Icon
                className={cn(
                  "h-5 w-5",
                  isSelected ? "text-primary" : "text-muted-foreground",
                )}
              />
              <div className="min-w-0">
                <span className="block text-sm font-medium">{title}</span>
                <span className="text-muted-foreground block text-xs">
                  {isDisabled ? skipBlockedReason : description}
                </span>
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
